import { useCallback } from 'react';
import { toast } from 'sonner';
import { useSetAgentMark } from '@/hooks/agents/useAgents';
import { AGENT_MARK_CONFIG } from '@/lib/agent-marks';
import type { Agent, AgentMark } from '@/lib/types';

/**
 * Apply or clear a run's triage mark, with the confirmation and failure toasts.
 *
 * Shared by every surface that offers the marks — the sidebar row's context
 * menu and the agent action list — so the copy and the error handling can't
 * drift between "Mark blocked" in one place and a silent failure in another.
 * The updated row arrives over the socket, so nothing here touches the cache.
 */
export function useApplyAgentMark(): (agent: Pick<Agent, 'id'>, mark: AgentMark | null) => void {
  const setMark = useSetAgentMark();
  return useCallback(
    (agent, mark) =>
      setMark.mutate(
        { id: agent.id, mark },
        {
          onSuccess: () =>
            toast.success(
              mark ? `Marked ${AGENT_MARK_CONFIG[mark].label.toLowerCase()}` : 'Mark cleared',
            ),
          onError: (err: Error) =>
            toast.error(mark ? 'Failed to mark agent' : 'Failed to clear mark', {
              description: err.message,
            }),
        },
      ),
    [setMark],
  );
}
